import React, { useEffect, useRef, useState } from "react";
import { Alert, Button, Col, Form, Input, Row, Select, Space, Tag, Typography, message } from "antd";
import { useQueryClient } from "@tanstack/react-query";
import { useAuth } from "../context/AuthContext";
import { useOptionalSelection } from "../SelectionContext";
import apiClient from "../api/apiClient";
import useBusinessResource from "../hooks/useBusinessResource";
import { invalidateBusinessResources } from "../utils/businessResourceFreshness";
import "./DailyRecapBriefingSettings.css";

const endpoint = "/api/users/me/whatsapp-recap-schedule";
const validSchedule = (value) => value && [0, 1].includes(value.dayOffset) &&
  /^(?:[01]\d|2[0-3]):[0-5]\d$/.test(value.time) && Number.isSafeInteger(value.revision);

export default function WhatsAppRecapScheduleSettings({ active }) {
  const { user } = useAuth();
  const selection = useOptionalSelection();
  const companyId = Number(selection?.selectedCompany?.id || selection?.selectedCompany || user?.company_id) || null;
  const client = useQueryClient();
  const [form] = Form.useForm();
  const [draft, setDraft] = useState(null);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState("");
  const initialRevision = useRef(null);
  const dirtyRef = useRef(false);
  const pending = useRef(null);
  const scope = JSON.stringify([user?.id, companyId, Boolean(active)]);
  const currentScope = useRef(scope);
  currentScope.current = scope;
  const resource = useBusinessResource({
    resource: "whatsapp-recap-schedule", moduleScope: "notifications", scopeLevel: "user",
    userId: user?.id, companyId: null, warehouseId: null, parameters: [companyId],
    enabled: Boolean(active && user?.id && companyId), active: Boolean(active),
    load: async (signal) => {
      const { data } = await apiClient.get(endpoint, { signal, params: { company_id: companyId } });
      if (typeof data.eligible !== "boolean" || typeof data.available !== "boolean" || !validSchedule(data.schedule)) {
        throw new Error("Horaire WhatsApp indisponible.");
      }
      return data;
    },
  });

  const applySchedule = (schedule) => {
    initialRevision.current = schedule.revision;
    dirtyRef.current = false;
    setDirty(false); setDraft(schedule); setSaveError("");
    form.setFieldsValue(schedule);
  };
  useEffect(() => {
    pending.current?.abort(); pending.current = null;
    initialRevision.current = null; dirtyRef.current = false;
    setDirty(false); setDraft(null); setSaving(false); setSaveError("");
    return () => { pending.current?.abort(); };
  }, [scope, form]);
  useEffect(() => {
    // Focus/reconnection refreshes never overwrite an in-progress choice.
    if (active && resource.data && !dirtyRef.current) applySchedule(resource.data.schedule);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, resource.data, scope]);

  const reload = async () => {
    const startedScope = scope;
    try {
      const data = await resource.refresh({ force: true });
      if (currentScope.current === startedScope) applySchedule(data.schedule);
    } catch (_) { /* Resource error and retry action stay visible. */ }
  };
  const save = async (values) => {
    if (pending.current || resource.status !== "verified" || !resource.data?.eligible || !resource.data.available) return;
    const startedScope = scope;
    const controller = new AbortController();
    pending.current = controller; setSaving(true); setSaveError("");
    try {
      const { data } = await apiClient.put(endpoint, { companyId, dayOffset: values.dayOffset,
        time: values.time, revision: initialRevision.current }, { signal: controller.signal });
      if (currentScope.current !== startedScope) return;
      if (!validSchedule(data.schedule)) throw new Error("INVALID_RESPONSE");
      applySchedule(data.schedule);
      message.success("Horaire des bilans WhatsApp enregistré.");
      await invalidateBusinessResources(client, { userId: user.id, companyId: null, warehouseId: null,
        moduleScope: "notifications" }, "whatsapp-recap-schedule");
    } catch (error) {
      if (currentScope.current === startedScope && error.code !== "ERR_CANCELED") {
        setSaveError(error.response?.data?.error || "L’horaire n’a pas pu être enregistré. Votre saisie est conservée.");
      }
    } finally {
      if (currentScope.current === startedScope) { pending.current = null; setSaving(false); }
    }
  };
  const conflict = dirty && resource.data && initialRevision.current !== resource.data.schedule.revision;
  const unavailable = !resource.data?.eligible || !resource.data.available;

  return <section className="daily-recap-briefing-settings" aria-label="Bilans quotidiens WhatsApp">
    <div className="daily-recap-briefing-settings__heading">
      <div><h3>Bilans quotidiens WhatsApp</h3>
        <p>Choisissez quand recevoir vos chiffres clés. Un message est envoyé par établissement abonné.</p></div>
      {resource.data?.stopped && <Tag color="warning">Désabonné par STOP</Tag>}
    </div>
    {!companyId ? <Alert type="info" showIcon message="Sélectionnez une entreprise pour consulter vos réglages." /> : <>
      {resource.error && <Alert type="warning" showIcon
        message="L’horaire n’a pas pu être actualisé. Votre saisie est conservée."
        action={<Button size="small" onClick={() => resource.refresh({ force: true }).catch(() => {})}>Réessayer</Button>} />}
      {resource.data && !resource.data.available && <Alert type="info" showIcon
        message="La personnalisation des horaires n’est pas encore disponible. L’horaire habituel reste appliqué." />}
      {resource.data && !resource.data.eligible && <Alert type="info" showIcon
        message={resource.data.stopped ? "Ce numéro est désabonné par STOP. Changer l’horaire ne réactive pas les bilans."
          : "L’administrateur doit vous abonner à au moins un établissement de cette entreprise et vous donner accès au bilan et aux notifications."} />}
      {resource.loading && !resource.data && <Typography.Text type="secondary">Chargement de votre horaire…</Typography.Text>}
      <Form form={form} layout="vertical" onFinish={save} style={draft ? undefined : { display: "none" }}
        onValuesChange={(_, values) => { dirtyRef.current = true; setDirty(true); setDraft(values); setSaveError(""); }}>
        <Row gutter={16}>
          <Col xs={24} sm={14}><Form.Item name="dayOffset" label="Jour de réception" rules={[{ required: true }]}>
            <Select disabled={saving || unavailable} options={[
              { value: 1, label: "Le lendemain (J+1)" }, { value: 0, label: "Le jour même (J)" },
            ]} />
          </Form.Item></Col>
          <Col xs={24} sm={10}><Form.Item name="time" label="Heure de réception" rules={[
            { required: true, message: "Choisissez une heure." },
            { pattern: /^(?:[01]\d|2[0-3]):[0-5]\d$/, message: "Utilisez le format HH:MM." },
          ]}>
            <Input type="time" step={60} disabled={saving || unavailable} />
          </Form.Item></Col>
        </Row>
        <div className="daily-recap-briefing-settings__note" style={{ marginBottom: 16 }}>
          <strong>Heure de Ouagadougou (UTC+0).</strong>{" "}
          {draft?.dayOffset === 0 ? `Bilan du jour vers ${draft.time || "l’heure choisie"}, avec les opérations disponibles à l’envoi. Les opérations suivantes n’y figureront pas.`
            : `Bilan de la journée complète de la veille vers ${draft?.time || "l’heure choisie"}.`}
          <br />Ce réglage personnel s’applique à tous vos établissements abonnés, dans toutes vos entreprises.
          L’administrateur conserve le choix des établissements et des destinataires.
        </div>
        {(conflict || saveError) && <Alert style={{ marginBottom: 16 }} type="warning" showIcon
          message={saveError || "L’horaire a changé depuis une autre session. Votre saisie est conservée."}
          action={<Button size="small" disabled={saving} onClick={reload}>Recharger le réglage</Button>} />}
        <Space wrap>
          <Button type="primary" htmlType="submit" loading={saving}
            disabled={!dirty || unavailable || resource.status !== "verified" || Boolean(conflict)}>Enregistrer mon horaire</Button>
          {dirty && <Button disabled={saving} onClick={reload}>Annuler mes changements</Button>}
        </Space>
      </Form>
    </>}
  </section>;
}
